import { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, ImagePlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CATEGORIES = ["Moisturiser", "Serum", "Lash & Brow", "Cleanser", "Tanning", "Eye Care", "SPF", "Mask", "Gift Cards", "Other"];
const BADGES = ["In Stock", "Low Stock", "Preorder", "Coming Soon"];
const EMPTY = { name: "", category: "Moisturiser", description: "", price: "", cogs: "", size: "", image_url: "", stock: 0, badge: "In Stock", active: true };

export default function Products() {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.get("/admin/products").then(({ data }) => setProducts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("image_url", data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0, cogs: parseFloat(form.cogs) || 0, stock: parseInt(form.stock) || 0 };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? "Product updated" : "Product added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between mb-10">
        <div>
          <h1 className="font-serif text-3xl md:text-4xl font-light mb-2">Products</h1>
          <p className="text-sm text-[#4A4A4A]">{products.length} products in your catalogue.</p>
        </div>
        <button onClick={openNew} data-testid="add-product-button"
          className="flex items-center gap-2 bg-[#1A1A1A] text-white text-xs tracking-[0.2em] uppercase px-5 py-3 hover:bg-[#9E7B3A] transition-colors duration-300">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      <div className="bg-white border border-[#C5A059]/25 overflow-x-auto">
        <table className="w-full text-sm" data-testid="products-table">
          <thead>
            <tr className="border-b border-[#C5A059]/20 text-left">
              {["", "Name", "Category", "Price", "Stock", "Badge", "Active", ""].map((h, i) => (
                <th key={i} className="px-5 py-4 text-[10px] tracking-[0.25em] uppercase text-[#9E7B3A] font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#C5A059]/10">
            {products.map((p) => (
              <tr key={p.id} data-testid={`product-row-${p.id}`}>
                <td className="px-5 py-3">
                  <img src={p.image_url || "/assets/logo.png"} alt={p.name} className="w-11 h-11 object-cover bg-[#FAFAFA]" />
                </td>
                <td className="px-5 py-3">
                  <p className="font-medium">{p.name}</p>
                  <p className="text-xs text-[#4A4A4A]">{p.size}</p>
                </td>
                <td className="px-5 py-3 text-[#4A4A4A]">{p.category}</td>
                <td className="px-5 py-3">${p.price.toFixed(2)}</td>
                <td className="px-5 py-3">{p.stock}</td>
                <td className="px-5 py-3"><span className="text-xs text-[#9E7B3A]">{p.badge}</span></td>
                <td className="px-5 py-3">{p.active ? "Yes" : "No"}</td>
                <td className="px-5 py-3">
                  <div className="flex gap-3">
                    <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} aria-label="Edit"
                      className="text-[#4A4A4A] hover:text-[#9E7B3A] transition-colors duration-300"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(p)} data-testid={`delete-product-${p.id}`} aria-label="Delete"
                      className="text-[#4A4A4A] hover:text-red-700 transition-colors duration-300"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-none" data-testid="product-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light">{editing ? "Edit Product" : "New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Name</Label>
              <Input required value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" className="rounded-none" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Category</Label>
                <Select value={form.category} onValueChange={(v) => set("category", v)}>
                  <SelectTrigger data-testid="product-category-select" className="rounded-none"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Size</Label>
                <Input value={form.size} onChange={(e) => set("size", e.target.value)} placeholder="50ml" data-testid="product-size-input" className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Price (AUD)</Label>
                <Input required type="number" step="0.01" min="0" value={form.price} onChange={(e) => set("price", e.target.value)} data-testid="product-price-input" className="rounded-none" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">COGS (AUD)</Label>
                <Input type="number" step="0.01" min="0" value={form.cogs} onChange={(e) => set("cogs", e.target.value)} data-testid="product-cogs-input" className="rounded-none" placeholder="Cost per unit" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs tracking-widest uppercase">Stock</Label>
                <Input type="number" min="0" value={form.stock} onChange={(e) => set("stock", e.target.value)} data-testid="product-stock-input" className="rounded-none" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Badge</Label>
              <Select value={form.badge} onValueChange={(v) => set("badge", v)}>
                <SelectTrigger data-testid="product-badge-select" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent>{BADGES.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Product Image</Label>
              {form.image_url && (
                <img src={form.image_url} alt="Preview" className="w-24 h-24 object-cover border border-[#C5A059]/30 mb-2" data-testid="product-image-preview" />
              )}
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={uploadImage} data-testid="product-image-file" />
              <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="product-image-upload-button"
                className="flex items-center gap-2 border border-[#C5A059]/40 text-xs tracking-[0.2em] uppercase px-4 py-2.5 hover:border-[#9E7B3A] hover:text-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
                <ImagePlus className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload Image"}
              </button>
              <Input value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="…or paste an image URL" data-testid="product-image-input" className="rounded-none mt-2" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs tracking-widest uppercase">Description</Label>
              <Textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3} data-testid="product-description-input" className="rounded-none" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => set("active", v)} data-testid="product-active-switch" />
              <Label className="text-xs tracking-widest uppercase">Visible in store</Label>
            </div>
            <button type="submit" disabled={saving} data-testid="product-save-button"
              className="w-full bg-[#1A1A1A] text-white text-xs tracking-[0.3em] uppercase py-3.5 hover:bg-[#9E7B3A] disabled:opacity-50 transition-colors duration-300">
              {saving ? "Saving…" : editing ? "Save Changes" : "Add Product"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
